import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import classnames from 'classnames';

import LogoNav from 'dibs-logo/exports/LogoNav.ts';
import Notifications from '../Notifications/Notifications.jsx';
import { UserNotifications } from '../Notifications/userNotifications/UserNotifications.tsx';
import { SearchBar } from '../SearchBar/SearchBar.jsx';
import AccountNav from '../AccountNav/AccountNav.tsx';
import Likes from '../Likes/Likes.tsx';
import Favorites from '../Favorites/Favorites.tsx';
import CartNav from '../CartNav/CartNav.jsx';
import { useUpdatedNotificationContentVariant } from '../../helpers/notificationsTestHelpers.ts';

import dibsCss from 'dibs-css';
import styles from './TopBar-style.scss';

const TopBarComponent = props => {
    const {
        hasRootPendingRefetch,
        hasUserId,
        hasUser,
        isClient,
        user,
        userId,
        viewer,
        onMouseOver,
        onMouseOut,
        hasError,
        isEmailOnly,
        isSticky,
        hasTradeUserId,
    } = props;
    const barClasses = classnames(styles.bar, dibsCss.zBump, {
        [styles.stickyBar]: isSticky,
    });
    const isUpdatedNotificationContentVariant = useUpdatedNotificationContentVariant();

    if (hasError) {
        return (
            <div className={barClasses}>
                <div
                    className={classnames(styles.barContent, dibsCss.flex, dibsCss.justifyBetween)}
                >
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.grow,
                            dibsCss.shrink,
                            dibsCss.basis0
                        )}
                    >
                        <LogoNav />
                    </div>
                </div>
            </div>
        );
    }

    return (
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <div className={barClasses} onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
            <div
                className={classnames(
                    styles.barContent,
                    dibsCss.flex,
                    dibsCss.justifyBetween,
                    !isSticky && dibsCss.ptXsmall
                )}
                data-tn="main-nav-top"
            >
                <LogoNav isStickyLogo={isSticky} />
                <div className={classnames(dibsCss.wFull, dibsCss.mxLarger, dibsCss.mrSmall)}>
                    <SearchBar
                        hasClickableSearchButton
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        isClient={isClient}
                        viewer={viewer}
                    />
                </div>
                <div
                    className={classnames(
                        styles.rightContainer,
                        dibsCss.flex,
                        dibsCss.justifyEnd,
                        dibsCss.itemsCenter,
                        dibsCss.grow,
                        dibsCss.shrink0,
                        styles.basis261
                    )}
                >
                    <AccountNav
                        isClient={isClient}
                        hasUser={hasUser}
                        hasUserId={hasUserId}
                        user={user}
                        hasTradeUserId={hasTradeUserId}
                        viewer={viewer}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                    />
                    {!isEmailOnly && isUpdatedNotificationContentVariant === false && (
                        <Notifications
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            hasUserId={hasUserId}
                            isClient={isClient}
                            userId={userId}
                            viewer={viewer}
                            initialViewer={viewer}
                            user={user}
                        />
                    )}
                    {!isEmailOnly &&
                        isUpdatedNotificationContentVariant === true &&
                        userId &&
                        isClient && (
                            <UserNotifications
                                userId={userId}
                                hasTradeUserId={hasTradeUserId}
                                hasRootPendingRefetch={hasRootPendingRefetch}
                            />
                        )}
                    {isUpdatedNotificationContentVariant === false && (
                        <Likes
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            hasUserId={hasUserId}
                            fetchNewEvents={hasUserId && isClient && !hasRootPendingRefetch}
                            isClient={isClient}
                            userId={userId}
                            viewer={viewer}
                        />
                    )}
                    {isUpdatedNotificationContentVariant === true && (
                        <Favorites
                            hasRootPendingRefetch={hasRootPendingRefetch}
                            hasUserId={hasUserId}
                            isClient={isClient}
                            userId={userId}
                        />
                    )}
                    <CartNav
                        hasRootPendingRefetch={hasRootPendingRefetch}
                        hasUserId={hasUserId}
                        isClient={isClient}
                        user={user}
                        userId={userId}
                        viewer={viewer}
                    />
                </div>
            </div>
        </div>
    );
};

TopBarComponent.propTypes = {
    hasError: PropTypes.bool,
    hasRootPendingRefetch: PropTypes.bool,
    hasUser: PropTypes.bool,
    hasUserId: PropTypes.bool,
    isClient: PropTypes.bool,
    openDrawer: PropTypes.func,
    user: PropTypes.object,
    userId: PropTypes.string,
    viewer: PropTypes.object,
    onMouseOver: PropTypes.func,
    onMouseOut: PropTypes.func,
    isEmailOnly: PropTypes.bool,
    isSticky: PropTypes.bool,
    hasTradeUserId: PropTypes.bool,
};

const mapStateToProps = ({ header }) => ({
    isEmailOnly: header.isEmailOnly,
});

export const TopBar = createFragmentContainer(connect(mapStateToProps)(TopBarComponent), {
    viewer: graphql`
        fragment TopBar_viewer on Viewer {
            ...CartNav_viewer
            ...Likes_viewer @skip(if: $isUpdatedNotificationContentVariant)
            ...Notifications_viewer @skip(if: $isUpdatedNotificationContentVariant)
            ...Notifications_initialViewer @skip(if: $isUpdatedNotificationContentVariant)
            ...SearchBar_viewer
            ...AccountNav_viewer
        }
    `,

    user: graphql`
        fragment TopBar_user on User {
            ... @include(if: $isClient) {
                ...AccountNav_user
                ...CartNav_user
                ...Notifications_user @skip(if: $isUpdatedNotificationContentVariant)
            }
        }
    `,
});
